import { achsenSchritte, balkenPfad, useGroesse, useHinweis } from './diagramm'
import { Hinweis, HinweisZeile } from './Hinweis'

export interface BalkenWert {
  schluessel: string
  etikett: string
  wert: number
  neben?: string
}

/**
 * Senkrechte Balken - Umsatz je Stunde, je Wochentag.
 *
 * Eine Reihe, also keine Legende: der Titel der Karte nennt sie. Die
 * Beschriftung steht nur am hoechsten Balken; eine Zahl ueber jedem
 * Balken macht aus dem Diagramm eine schlecht gesetzte Tabelle.
 */
export function Balken({
  werte, hoehe, farbe = 'var(--folge-4)', formatiere, formatiereAchse,
  wertName = 'Umsatz', nebenName = 'Rechnungen', xAchsenTitel,
}: {
  werte: BalkenWert[]
  hoehe?: number
  farbe?: string
  formatiere: (w: number) => string
  formatiereAchse: (w: number) => string
  wertName?: string
  nebenName?: string
  xAchsenTitel?: string
}) {
  const [ref, groesse] = useGroesse<HTMLDivElement>()
  const breite = groesse.breite
  const h = useHinweis<BalkenWert>()
  const diagrammHoehe = hoehe ?? Math.max(250, groesse.hoehe)

  const links = 52, rechts = 8, oben = 14, unten = xAchsenTitel ? 38 : 24
  const zeichenBreite = Math.max(0, breite - links - rechts)
  const zeichenHoehe = diagrammHoehe - oben - unten
  const max = Math.max(0, ...werte.map((w) => w.wert))
  const { schritte, obergrenze } = achsenSchritte(max)
  const y = (v: number) => oben + zeichenHoehe * (1 - v / (obergrenze || 1))

  // 2px Luft zwischen benachbarten Flaechen, damit zwei Balken nicht
  // zu einem verschmelzen.
  const schrittBreite = werte.length ? zeichenBreite / werte.length : 0
  const balkenBreite = Math.max(2, Math.min(38, schrittBreite - 4))
  const hoechster = werte.reduce((a, b) => (b.wert > a.wert ? b : a), werte[0])

  return (
    <div ref={ref} className="balkendiagramm">
      {breite > 0 && (
        <svg className="diagramm" height={diagrammHoehe} role="img"
             aria-label={`Balkendiagramm mit ${werte.length} Werten`}>
          {schritte.map((s) => (
            <g key={s}>
              <line className={s === 0 ? 'grundlinie' : 'gitterlinie'}
                    x1={links} x2={links + zeichenBreite} y1={y(s)} y2={y(s)} />
              <text className="achsentext" x={links - 8} y={y(s) + 3}
                    textAnchor="end">{formatiereAchse(s)}</text>
            </g>
          ))}
          {werte.map((w, i) => {
            const x = links + i * schrittBreite + (schrittBreite - balkenBreite) / 2
            const oben_ = y(w.wert)
            const istHoechster = hoechster && w.schluessel === hoechster.schluessel
            return (
              <g key={w.schluessel}>
                <path d={balkenPfad(x, oben_, balkenBreite, y(0) - oben_)}
                      fill={farbe} />
                {/* Trefferflaeche groesser als die Marke */}
                <rect x={links + i * schrittBreite} y={oben}
                      width={schrittBreite} height={zeichenHoehe}
                      fill="transparent"
                      onMouseMove={(e) => h.zeigen(w, e)}
                      onMouseLeave={h.verbergen} />
                {istHoechster && w.wert > 0 && (
                  <text className="achsentext" x={x + balkenBreite / 2}
                        y={oben_ - 5} textAnchor="middle"
                        style={{ fill: 'var(--schrift-2)', fontWeight: 500 }}>
                    {formatiereAchse(w.wert)}
                  </text>
                )}
                <text className="achsentext" x={x + balkenBreite / 2}
                      y={diagrammHoehe - (xAchsenTitel ? 22 : 8)}
                      textAnchor="middle">{w.etikett}</text>
              </g>
            )
          })}
          {xAchsenTitel && (
            <text className="achsentext" x={links + zeichenBreite}
                  y={diagrammHoehe - 4} textAnchor="end">{xAchsenTitel}</text>
          )}
        </svg>
      )}
      {h.inhalt && (
        <Hinweis lage={h.lage}>
          <div className="titel">{h.inhalt.etikett}</div>
          <HinweisZeile name={wertName} wert={formatiere(h.inhalt.wert)} />
          {h.inhalt.neben && <HinweisZeile name={nebenName} wert={h.inhalt.neben} />}
        </Hinweis>
      )}
    </div>
  )
}
